import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable, firebaseFunctions } from '../services/firebaseService';
import { multiFactor } from 'firebase/auth';

export const useSidebarLogic = (onViewChange: (view: string) => void) => {
    const { currentUser, isSuperAdmin, logout } = useAuth();
    const { t } = useTranslation();
    // Meldungen des Backends sind künftig Übersetzungsschlüssel (backend.errors.*)
    const errorText = (e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e ?? '');
        return msg ? t(msg, { defaultValue: msg }) : t('common.unknownError');
    };

    // Profile Edit State
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mfaLoading, setMfaLoading] = useState(false);

    const [showMfaDeactivateModal, setShowMfaDeactivateModal] = useState(false);
    const [showMfaDeactivateSuccess, setShowMfaDeactivateSuccess] = useState(false);

    // MFA State
    const enrolledFactors = currentUser ? multiFactor(currentUser).enrolledFactors : [];
    const isMfaActive = enrolledFactors.length > 0;

    // Check if MFA is enforced by global settings, but super admin can always disable it
    // We need to pass isMfaEnforced or fetch it, but usually it comes from props/context
    // For now we assume logic handles it based on props passed to Sidebar
    // But since this hook is decoupled, we'll return a helper to toggle

    const handleLogout = () => logout();

    const openProfileModal = () => {
        setShowProfileModal(true);
    };

    const closeProfileModal = () => setShowProfileModal(false);

    const handleSaveProfile = async (data: {
        displayName: string,
        language?: string,
        emailPreferences?: {
            commentNotifications?: 'immediate' | 'daily' | 'none',
            taskReminders?: boolean
        }
    }) => {
        if (!currentUser) return;

        if (!firebaseFunctions) {
            alert(t('common.errors.backendMissing', 'Fehler: Backend-Verbindung fehlt.'));
            return;
        }

        setIsSaving(true);
        try {
            const updateFn = httpsCallable(firebaseFunctions, 'updateuserprofile');
            await updateFn({ displayName: data.displayName, language: data.language, emailPreferences: data.emailPreferences });
            
            // Reload user profile locally to apply language immediately without full refresh
            if (currentUser) {
                 await currentUser.reload();
                 // If using react-i18next, import standard changeLanguage might not be possible here because it's a hook, we rely on App.tsx effect 
                 // or just reload window if language changed. Let's do a window.location.reload() for a clean language switch if needed,
                 // but typically the AuthContext listener catches the user update or we can just change i18n directly.
            }
            setShowProfileModal(false);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            console.error("Error updating profile", e);
            alert(t('sidebar.profileSaveError', { msg: errorText(e) }));
        } finally {
            setIsSaving(false);
        }
    };


    const handleToggleMfa = async (isMfaEnforced: boolean) => {
        if (!currentUser) return;

        const canDisableMfa = isMfaActive && (!isMfaEnforced || isSuperAdmin);

        if (isMfaActive) {
            // Disable MFA
            if (!canDisableMfa) return;
            // Instead of confirm() we show the modal
            setShowMfaDeactivateModal(true);
        } else {
            // Enable MFA
            setShowProfileModal(false);
            onViewChange('/mfa-setup');
        }
    };

    const closeMfaDeactivateModal = () => {
        setShowMfaDeactivateModal(false);
    };

    const closeMfaDeactivateSuccess = () => {
        setShowMfaDeactivateSuccess(false);
    };

    const handleConfirmDisableMfa = async () => {
        if (!currentUser || !isMfaActive) return;

        setMfaLoading(true);
        try {
            const factors = multiFactor(currentUser).enrolledFactors;
            for (const factor of factors) {
                await multiFactor(currentUser).unenroll(factor.uid);
            }
            // Success: reload user to update state without page reload
            await currentUser.reload();
            setShowMfaDeactivateSuccess(true);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            console.error("Unenroll failed", error);
            // We can keep the error alert or handle it better, but user asked to remove success alert/confirm
            // Keeping error alert is generally safer for UX if something breaks
            alert(t('sidebar.mfaDisableError', { msg: errorText(error) }));
        } finally {
            setMfaLoading(false);
            setShowMfaDeactivateModal(false);
        }
    };

    return {
        showProfileModal,
        isSaving,
        mfaLoading,
        isMfaActive,
        handleLogout,
        openProfileModal,
        closeProfileModal,
        handleSaveProfile,
        handleToggleMfa,
        // Exports for modal
        showMfaDeactivateModal,
        closeMfaDeactivateModal,
        handleConfirmDisableMfa,
        showMfaDeactivateSuccess,
        closeMfaDeactivateSuccess
    };
};
